import { Grid } from '@mui/material';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PickersDay } from '@mui/x-date-pickers';
import { DATE_FORMATS } from '@callmart/types';
import Container from '../components/Layout/Container';
import { useIsMounted, useLocationsByLoggedInUserRole, useCookieLocation, useAlertMessage } from '../hooks';
import { useDebounce } from '../hooks/useDebounce';
import { getAllChannelsFromCapacitiesByFilters } from '../services/channel';
import { getAllWorktypeByLocation } from '../services/worktype';
import { getAllTasksFromCapacitiesByFilters } from '../services/task';
import LocationSelect from '../components/Selects/LocationSelect';
import WorktypeSelect from '../components/Selects/WorktypeSelect';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import { getCapacityReportAnalysis, getCapacityReportUploadedDatesInMonth } from '../services/capacityReport';
import {
    getPerformedCapacityReportAnalysis,
    getPerformedCapacityReportUploadedDatesInMonth,
} from '../services/performedCapacityReport';
import { CapacityAnalysisType } from '../enums/CapacityAnalysisType';
import { CapacityAnalysisDateType } from '../enums/CapacityAnalysisDateType';
import CapacityHorizontalScrollTable from '../components/CapacityReport/CapacityHorizontalScrollTable';
import { createTableRows } from '../utils/agGridUtils';
import { createMapWithTableRows } from '../components/CapacityReport/helpers/utils';
import { CapacityAnalysisRowGroup } from '../components/CapacityReport/helpers/constants';
import { getAllCapacityTypesFromCapacitiesByFilters } from '../services/capacityType';
import { appTimezone } from '../config';
import GridContainer from '../components/Layout/GridContainer';
import ChannelSelect from '../components/Selects/ChannelSelect';
import TaskSelect from '../components/Selects/TaskSelect';
import CapacityTypeSelect from '../components/Selects/CapacityTypeSelect';
import dayjs from '@utils/dayjs';

const getMonthColumns = (startingMonth) => {
    const months = [];
    for (let i = 0; i < 6; i++) {
        months.push(dayjs(startingMonth, DATE_FORMATS.ISO_DATE).subtract(i, 'months'));
    }

    return months.sort((date1, date2) => date1.diff(date2));
};

const getSelectOptionsWithoutAllOption = (selectedOption, options) => {
    if (selectedOption === -1) {
        return options.filter((option) => option.id !== -1).map((option) => option.id);
    } else {
        return [selectedOption];
    }
};

// Hoisted out of render so the table's defaultColDef prop keeps a stable
// reference and AG Grid does not redefine its columns on every parent render.
const DEFAULT_COL_DEF = {
    editable: false,
    filter: true,
    sortable: true,
};

function CapacityAnalysis({ type }) {
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const { dispatchAlert } = useAlertMessage();

    const gridRef = useRef(null);

    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedWorktype, setSelectedWorktype] = useState(0);
    const [worktypeOptions, setWorktypeOptions] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(0);
    const [channelOptions, setChannelOptions] = useState([]);
    const [selectedTask, setSelectedTask] = useState(0);
    const [taskOptions, setTaskOptions] = useState([]);
    const [selectedCapacityType, setSelectedCapacityType] = useState(0);
    const [capacityTypeOptions, setCapacityTypeOptions] = useState([]);

    const [planDate, setPlanDate] = useState(dayjs());
    const [planComparisonDate, setPlanComparisonDate] = useState(dayjs());
    const [firstMonthColumnDate, setFirstMonthColumnDate] = useState(dayjs());

    const [tableRows, setTableRows] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);

    const planDatePickerUploadDates = useRef();
    const planComparisonDatePickerUploadDates = useRef();

    const reportsPropertyName =
        type === CapacityAnalysisType.PERFORMED_CAPACITY_ANALYSIS ? 'performedCapacityReports' : 'capacityReports';

    // Stable reference: keeps the table's goBack/goForwardMonth callbacks (and
    // therefore createColumnDefs) from being rebuilt on every render, which
    // otherwise forced a full AG Grid column redefine on each parent render.
    const getExpandedRowsInTable = useCallback(() => {
        const expandedRows = [];
        gridRef.current.api.forEachNode((node) => {
            if (node.displayed && node.expanded) {
                expandedRows.push(node);
            }
        });

        return expandedRows;
    }, []);

    const setUploadedDatesForDatePicker = (location, date, dateType, reportType) => {
        const getReportUploadedDatesInMonth =
            reportType === CapacityAnalysisType.PERFORMED_CAPACITY_ANALYSIS
                ? getPerformedCapacityReportUploadedDatesInMonth
                : getCapacityReportUploadedDatesInMonth;

        // Format date as full ISO 8601 string (start of German day) for consistency with other routes
        const dateStr = dayjs(date).tz(appTimezone).startOf('day').toISOString();
        getReportUploadedDatesInMonth({ location, date: dateStr }).then((res) => {
            if (dateType === CapacityAnalysisDateType.PLAN_COMPARISON_DATE) {
                planComparisonDatePickerUploadDates.current = res;
            } else {
                planDatePickerUploadDates.current = res;
            }
        });
    };

    useEffect(() => {
        if (selectedLocation && planDate) {
            setUploadedDatesForDatePicker(selectedLocation, planDate, CapacityAnalysisDateType.PLAN_DATE, type);
        }
    }, [selectedLocation, planDate, type]);

    useEffect(() => {
        if (selectedLocation && planComparisonDate) {
            setUploadedDatesForDatePicker(
                selectedLocation,
                planComparisonDate,
                CapacityAnalysisDateType.PLAN_COMPARISON_DATE,
                type
            );
        }
    }, [selectedLocation, planComparisonDate, type]);

    const fetchWorktypes = useCallback(
        (location) => {
            getAllWorktypeByLocation(location).then((res) => {
                const worktypes = [
                    {
                        id: -1,
                        worktype: t('filter.allWorktypes'),
                    },
                    ...res,
                ];

                setWorktypeOptions(worktypes);
                setSelectedWorktype(worktypes[0].id);
            });
        },
        [t]
    );

    const fetchChannels = useCallback(
        (location, worktypes) => {
            getAllChannelsFromCapacitiesByFilters(location, worktypes).then((res) => {
                const channels = [
                    {
                        id: -1,
                        name: t('filter.allChannels'),
                    },
                    ...res,
                ];
                setChannelOptions(channels);
                setSelectedChannel(channels[0].id);
            });
        },
        [t]
    );

    const fetchTasks = useCallback(
        (location, worktypes, channels) => {
            getAllTasksFromCapacitiesByFilters(location, worktypes, channels).then((res) => {
                const tasks = [
                    {
                        id: -1,
                        name: t('filter.allTasks'),
                    },
                    ...res,
                ];
                setTaskOptions(tasks);
                setSelectedTask(tasks[0].id);
            });
        },
        [t]
    );

    const fetchCapacityTypes = useCallback(
        (location, worktypes, channels, tasks) => {
            getAllCapacityTypesFromCapacitiesByFilters(location, worktypes, channels, tasks).then((res) => {
                const capacityTypes = [
                    {
                        id: -1,
                        name: t('filter.allCapacityTypes'),
                    },
                    ...res,
                ];
                setCapacityTypeOptions(capacityTypes);
                setSelectedCapacityType(capacityTypes[0].id);
            });
        },
        [t]
    );

    // The four cascading fetchers are intentionally omitted from these effects'
    // deps: each one closes over `t` for its synthetic "All ..." option label, so
    // a language switch changes their identity. Depending on them would re-run
    // the cascade and reset every filter to "All" (and refetch a different
    // report) just because the user changed language. The "All" labels pick up
    // the new language on the next real fetch.
    useEffect(() => {
        if (selectedLocation) {
            fetchWorktypes(selectedLocation);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, isMounted]);

    useEffect(() => {
        if (selectedLocation && selectedWorktype) {
            const worktypes = getSelectOptionsWithoutAllOption(selectedWorktype, worktypeOptions);

            if (worktypes.length > 0) {
                fetchChannels(selectedLocation, worktypes);
            } else {
                setChannelOptions([]);
                setSelectedChannel('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, selectedWorktype, worktypeOptions]);

    useEffect(() => {
        if (selectedLocation && selectedWorktype && selectedChannel) {
            const worktypes = getSelectOptionsWithoutAllOption(selectedWorktype, worktypeOptions);
            const channels = getSelectOptionsWithoutAllOption(selectedChannel, channelOptions);

            if (worktypes.length > 0 && channels.length > 0) {
                fetchTasks(selectedLocation, worktypes, channels);
            } else {
                setTaskOptions([]);
                setSelectedTask('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, selectedWorktype, selectedChannel, worktypeOptions, channelOptions]);

    useEffect(() => {
        if (selectedLocation && selectedWorktype && selectedChannel && selectedTask) {
            const worktypes = getSelectOptionsWithoutAllOption(selectedWorktype, worktypeOptions);
            const channels = getSelectOptionsWithoutAllOption(selectedChannel, channelOptions);
            const tasks = getSelectOptionsWithoutAllOption(selectedTask, taskOptions);

            if (worktypes.length > 0 && channels.length > 0 && tasks.length > 0) {
                fetchCapacityTypes(selectedLocation, worktypes, channels, tasks);
            } else {
                setCapacityTypeOptions([]);
                setSelectedCapacityType('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedLocation,
        selectedWorktype,
        selectedChannel,
        selectedTask,
        worktypeOptions,
        channelOptions,
        taskOptions,
    ]);

    const setRows = useCallback(
        (data, firstMonthColumnDate) => {
            const { reportPlanDate, reportPlanComparisonDate, reportDifference } = data;

            const monthColumns = getMonthColumns(firstMonthColumnDate);

            // `rowsHeader` is the AG Grid rowGroup field AND part of getRowId in
            // CapacityHorizontalScrollTable, so it must stay language-independent —
            // a translated value would make every row id change with the UI
            // language. The grid translates these keys for display.
            const rowsMapPlanDate = createMapWithTableRows(
                reportPlanDate,
                monthColumns,
                reportsPropertyName,
                CapacityAnalysisRowGroup.PLAN_STATUS
            );
            const reportPlanDateRows = createTableRows(rowsMapPlanDate, 'id');

            const rowsMapPlanComparisonDate = createMapWithTableRows(
                reportPlanComparisonDate,
                monthColumns,
                reportsPropertyName,
                CapacityAnalysisRowGroup.PLAN_STATUS_COMPARISON
            );
            const reportPlanComparisonDateRows = createTableRows(rowsMapPlanComparisonDate, 'id');

            const rowsMapDifference = createMapWithTableRows(
                reportDifference,
                monthColumns,
                reportsPropertyName,
                CapacityAnalysisRowGroup.DIFFERENCE
            );
            const reportDifferenceRows = createTableRows(rowsMapDifference, 'id');

            const allRows = [...reportPlanDateRows, ...reportPlanComparisonDateRows, ...reportDifferenceRows];

            setTableRows(allRows);
        },
        [reportsPropertyName]
    );

    const getTableData = useCallback(async () => {
        try {
            // Format dates as full ISO 8601 strings (start of German day) for consistency with other routes
            const params = {
                location: selectedLocation,
                planDate: dayjs(planDate).tz(appTimezone).startOf('day').toISOString(),
                planComparisonDate: dayjs(planComparisonDate).tz(appTimezone).startOf('day').toISOString(),
                firstMonthColumnDate: dayjs(firstMonthColumnDate).tz(appTimezone).startOf('day').toISOString(),
                type: null,
            };

            if (
                !selectedLocation ||
                !selectedWorktype ||
                !selectedChannel ||
                !selectedTask ||
                !selectedCapacityType ||
                !planDate ||
                !planComparisonDate ||
                !firstMonthColumnDate
            )
                return;

            selectedWorktype !== -1 && (params.worktype = selectedWorktype);
            selectedChannel !== -1 && (params.channel = selectedChannel);
            selectedTask !== -1 && (params.task = selectedTask);
            selectedCapacityType !== -1 && (params.capacityType = selectedCapacityType);
            const res =
                type === CapacityAnalysisType.PERFORMED_CAPACITY_ANALYSIS
                    ? await getPerformedCapacityReportAnalysis(params)
                    : await getCapacityReportAnalysis(params);
            setRows(res, firstMonthColumnDate);
        } catch (error) {
            dispatchAlert({
                message: t('analysis.reportLoadError'),
                type: 'error',
            });
        }
    }, [
        dispatchAlert,
        selectedLocation,
        selectedWorktype,
        selectedChannel,
        selectedTask,
        selectedCapacityType,
        planDate,
        planComparisonDate,
        firstMonthColumnDate,
        type,
        setRows,
        t,
    ]);

    // Coalesce the cascading filter changes (location → worktype → channel →
    // task → capacityType each reset the next) into a single fetch. Without
    // this, one user selection fired getTableData several times in a row.
    const fetchSignature = useMemo(
        () =>
            JSON.stringify({
                selectedLocation,
                selectedWorktype,
                selectedChannel,
                selectedTask,
                selectedCapacityType,
                planDate,
                planComparisonDate,
                firstMonthColumnDate,
                type,
            }),
        [
            selectedLocation,
            selectedWorktype,
            selectedChannel,
            selectedTask,
            selectedCapacityType,
            planDate,
            planComparisonDate,
            firstMonthColumnDate,
            type,
        ]
    );
    const debouncedFetchSignature = useDebounce(fetchSignature, 300);

    useEffect(() => {
        if (!selectedLocation) {
            return;
        }

        getTableData();
        // getTableData is intentionally omitted: it is recreated on every filter
        // change, so depending on it would defeat the debounce. The debounced
        // signature is the single source of truth for when to refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFetchSignature]);

    const renderDayInPicker = (day, value, DayComponentProps, dateType) => {
        let className = '';
        if (
            (dateType === CapacityAnalysisDateType.PLAN_DATE &&
                planDatePickerUploadDates.current?.includes(dayjs(day).format(DATE_FORMATS.DATE_HYPHEN))) ||
            (dateType === CapacityAnalysisDateType.PLAN_COMPARISON_DATE &&
                planComparisonDatePickerUploadDates.current?.includes(dayjs(day).format(DATE_FORMATS.DATE_HYPHEN)))
        ) {
            className = 'datepicker-circle-day';
        }

        return <PickersDay {...DayComponentProps} className={className} />;
    };

    const onMonthChangeDatePicker = (date, dateType, reportType) => {
        setUploadedDatesForDatePicker(selectedLocation, date, dateType, reportType);
    };

    return (
        <Container>
            <GridContainer>
                <Grid item xs={12} md={4} lg={3}>
                    <LocationSelect
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onChange={setSelectedLocation}
                    ></LocationSelect>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    <WorktypeSelect
                        selectedWorktype={selectedWorktype}
                        worktypeOptions={worktypeOptions}
                        onChange={setSelectedWorktype}
                    />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    <ChannelSelect
                        selectedChannel={selectedChannel}
                        channelOptions={channelOptions}
                        onChange={setSelectedChannel}
                    />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    <TaskSelect selectedTask={selectedTask} taskOptions={taskOptions} onChange={setSelectedTask} />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    <CapacityTypeSelect
                        selectedCapacityType={selectedCapacityType}
                        capacityTypeOptions={capacityTypeOptions}
                        onChange={setSelectedCapacityType}
                    />
                </Grid>
                <Grid item xs={12} md={4} lg={2}>
                    <CustomDatePicker
                        date={planDate}
                        label={t('analysis.planStatus')}
                        onChange={setPlanDate}
                        renderDay={(day, value, DayComponentProps) =>
                            renderDayInPicker(day, value, DayComponentProps, CapacityAnalysisDateType.PLAN_DATE)
                        }
                        onMonthChange={(value) =>
                            onMonthChangeDatePicker(value, CapacityAnalysisDateType.PLAN_DATE, type)
                        }
                    ></CustomDatePicker>
                </Grid>
                <Grid item xs={12} md={4} lg={2}>
                    <CustomDatePicker
                        date={planComparisonDate}
                        label={t('analysis.planStatusComparison')}
                        onChange={setPlanComparisonDate}
                        renderDay={(day, value, DayComponentProps) =>
                            renderDayInPicker(
                                day,
                                value,
                                DayComponentProps,
                                CapacityAnalysisDateType.PLAN_COMPARISON_DATE
                            )
                        }
                        onMonthChange={(value) =>
                            onMonthChangeDatePicker(value, CapacityAnalysisDateType.PLAN_COMPARISON_DATE)
                        }
                    ></CustomDatePicker>
                </Grid>
                <CapacityHorizontalScrollTable
                    ref={gridRef}
                    firstMonth={firstMonthColumnDate}
                    onFirstMonthChanged={setFirstMonthColumnDate}
                    tableRows={tableRows}
                    expandedRows={expandedRows}
                    onExpandedRowsChanged={setExpandedRows}
                    getExpandedRowsInTable={getExpandedRowsInTable}
                    defaultColDef={DEFAULT_COL_DEF}
                    tableType="CAPACITY_ANALYSIS"
                    suppressAggFuncInHeader={true}
                    groupDefaultExpanded={1}
                    getMonthColumns={getMonthColumns}
                ></CapacityHorizontalScrollTable>
            </GridContainer>
        </Container>
    );
}

export default CapacityAnalysis;
